import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { LoadingIndicator } from '../../../components/Tools/LoadingIndicator';
import axios from 'axios';
import SubscriptionOnboarding from './SubscriptionOnboarding';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const SubscriptionGate = ({ children }) => {
  const { idToken, user } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // Helper function to convert Firestore timestamp to Date
  const convertFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle Firestore timestamp object
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000);
    }
    
    // Handle regular timestamp or date string
    return new Date(timestamp);
  };

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!idToken || !user) {
        setLoading(false);
        return;
      }

      try {
        // First check for special user access
        const specialAccessResponse = await axios.get(
          `${BACKEND_API_URL}/api/auth/user/special-access`,
          {
            headers: { Authorization: `Bearer ${idToken}` }
          }
        );

        if (specialAccessResponse.data.hasSpecialAccess) {
          setHasActiveSubscription(true);
          return;
        }

        // If not a special user, check regular subscription status
        const subscriptionResponse = await axios.get(
          `${BACKEND_API_URL}/api/auth/user/subscription`,
          {
            headers: { Authorization: `Bearer ${idToken}` }
          }
        );

        const subscription = subscriptionResponse.data.subscription;
        
        if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
          // Check if trial period is still valid or it's a paid subscription
          const currentDate = new Date();
          const periodEnd = convertFirestoreTimestamp(subscription.currentPeriodEnd);
          
          if (periodEnd && currentDate <= periodEnd) {
            setHasActiveSubscription(true);
          } else {
            setHasActiveSubscription(false);
          }
        } else {
          setHasActiveSubscription(false);
        }
      } catch (error) {
        setHasActiveSubscription(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [idToken, user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingIndicator showLogo={true} />
      </div>
    );
  }

  // If user doesn't have active subscription, show onboarding
  if (!hasActiveSubscription) {
    return <SubscriptionOnboarding />;
  }

  // If user has active subscription, render children
  return children;
};

export default SubscriptionGate; 